import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

// Morning Briefing — the assistant's daily digest, on demand. Same data emailed at market-open:
// positions needing attention, your book vs exit plans, this week's earnings, top picks, Auto Calls P&L.
export default function Briefing() {
  const { data, isLoading, refetch, isFetching } = useQuery({ queryKey: ['briefing'], queryFn: () => api.positions.briefing(), refetchInterval: 5 * 60_000 })
  const money = (n: number | null | undefined) => n == null ? '—' : `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(2)}`
  const clr = (n: number | null | undefined) => n == null ? 'var(--muted)' : n >= 0 ? 'var(--cash)' : 'var(--red)'
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 16px' }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  )
  const g = data?.guardian, f = data?.folio

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Morning Briefing</h1>
          <div className="eyebrow">{data?.date ?? 'today'} · emailed each market-open{isFetching && !isLoading ? ' · refreshing…' : ''}</div>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} style={{
          padding: '7px 16px', background: isFetching ? 'var(--stamp)' : 'var(--cash)', color: isFetching ? 'var(--muted)' : 'var(--ink)',
          border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem',
          cursor: isFetching ? 'wait' : 'pointer', whiteSpace: 'nowrap',
        }}>{isFetching ? '↻ Refreshing…' : '↻ Refresh brief'}</button>
      </div>

      {isLoading && <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>Assembling…</div>}

      {data?.triggered?.length > 0 && (
        <div style={{ background: 'rgba(212,168,67,.08)', border: '1px solid var(--amber)', borderRadius: 4, padding: '14px 16px' }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.85rem', color: 'var(--amber)', marginBottom: 8 }}>⚠ {data.triggered.length} position{data.triggered.length > 1 ? 's need' : ' needs'} attention</div>
          {data.triggered.map((p: any) => (
            <div key={p.id} style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)', padding: '3px 0' }}>
              {p.ticker} ${p.strike}{p.optionType === 'put' ? 'P' : 'C'} — <b>{p.triggered}</b> · <span style={{ color: clr(p.pnl) }}>{money(p.pnl)}</span>
            </div>
          ))}
          <Link to="/positions" style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)', textDecoration: 'none' }}>Review in Positions →</Link>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
        {g && (
          <Section title={`Your positions (${g.count}) · ${money(g.totalPnl)}`}>
            {g.positions.length === 0 ? <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)' }}>None guarded. <Link to="/positions" style={{ color: 'var(--cash)', textDecoration: 'none' }}>Add some →</Link></div> :
              g.positions.map((p: any) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontFamily: 'Syne Mono', fontSize: '.62rem', borderBottom: '1px solid var(--edge)' }}>
                  <span style={{ color: 'var(--text)' }}>{p.ticker} ${p.strike}{p.optionType === 'put' ? 'P' : 'C'} <span style={{ color: 'var(--muted)' }}>{p.dte ?? '?'}d</span></span>
                  <span style={{ color: clr(p.pnl), fontWeight: 700 }}>{money(p.pnl)}{p.triggered ? ' ⚠' : ''}</span>
                </div>
              ))}
          </Section>
        )}

        {f?.stats && (
          <Section title="Auto Calls (paper)">
            {[
              ['Total P&L', money(f.stats.realized + f.stats.unrealized), clr(f.stats.realized + f.stats.unrealized)],
              ['Realized', money(f.stats.realized), clr(f.stats.realized)],
              ['Open / Closed', `${f.stats.openCount} / ${f.stats.closedCount}`, 'var(--text)'],
              ['Win rate', f.stats.winRate != null ? `${f.stats.winRate}%` : '—', 'var(--text)'],
            ].map(([l, v, c]: any) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontFamily: 'Syne Mono', fontSize: '.62rem', borderBottom: '1px solid var(--edge)' }}>
                <span style={{ color: 'var(--muted)' }}>{l}</span><span style={{ color: c, fontWeight: 700 }}>{v}</span>
              </div>
            ))}
          </Section>
        )}

        {data?.earnings?.length > 0 && (
          <Section title="⚡ Earnings this week">
            {data.earnings.map((e: any) => (
              <div key={e.ticker} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontFamily: 'Syne Mono', fontSize: '.62rem', borderBottom: '1px solid var(--edge)' }}>
                <span style={{ color: 'var(--text)' }}>{e.ticker}</span><span style={{ color: 'var(--amber)' }}>{e.date}</span>
              </div>
            ))}
          </Section>
        )}

        {data?.picks?.length > 0 && (
          <Section title="Today's top calls">
            {data.picks.map((p: any) => (
              <div key={`${p.ticker}-${p.strike}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontFamily: 'Syne Mono', fontSize: '.62rem', borderBottom: '1px solid var(--edge)' }}>
                <span style={{ color: 'var(--text)' }}>{p.ticker} ${p.strike}C <span style={{ color: 'var(--muted)' }}>{p.expiry}</span></span>
                <span style={{ color: 'var(--cash)', fontWeight: 700 }}>score {p.score != null ? Math.round(p.score) : '—'}</span>
              </div>
            ))}
            <Link to="/scanner" style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--cash)', textDecoration: 'none' }}>Open scanner →</Link>
          </Section>
        )}
      </div>
    </div>
  )
}
